import { Box, Typography } from '@material-ui/core';
import { CheckCircle, Error } from '@material-ui/icons';

const STATUS_UI = {
    GREEN: {
        border: '#4CAF50',
        background: '#4CAF50',
        color: '#fff',
        icon: <CheckCircle style={{ color: '#fff', fontSize: 26 }} />,
    },
    RED: {
        border: '#F44336',
        background: '#F44336',
        color: '#fff',
        icon: <Error style={{ color: '#fff', fontSize: 26 }} />,
    },
    DEFAULT: {
        border: '#F1F3F6',
        background: '#F1F3F6',
        color: '#000',
        icon: <Error style={{ color: '#43419E', fontSize: 26 }} />,
    },
};

export const formatBGVTimestamp = (isoString) => {
    if (!isoString) return '';

    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';

    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');

    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;

    const formattedTime = `${hours
        .toString()
        .padStart(2, '0')}:${minutes} ${ampm}`;

    return `${day} ${month} ${year} ,  ${formattedTime}`;
};

const mapSeverityToLabel = (caseSeverity) => {
    const s = (caseSeverity || '').trim().toLowerCase();
    if (s === 'bgv pending') return 'INITIATED';
    if (s === 'clear') return 'VERIFIED';
    if (s === 'insufficient') return 'INSUFFICIENT';
    if (s === 'discrepant' || s === 'discrepancy') return 'DISCREPANT';
    return (caseSeverity || '--').toUpperCase();
};

const mapStatusToUI = (item) => {
    const { state, initiatedAt, status = '', caseSeverity = '' } = item;

    const normalizedStatus = status?.trim()?.toLowerCase();
    const formattedTime = formatBGVTimestamp(initiatedAt);

    if (normalizedStatus === 'interim' || normalizedStatus === 'final') {
        return {
            label: mapSeverityToLabel(caseSeverity),
            time: formattedTime,
            token: STATUS_UI.DEFAULT,
        };
    }

    return {
        label: (state || 'Unknown Status').toUpperCase(),
        time: formattedTime,
        token: STATUS_UI.DEFAULT,
    };
};

const TimelineStep = ({ label, time, token, showConnector }) => {
    return (
        <Box display="flex" flexDirection="column">
            <Box
                style={{
                    borderRadius: 8,
                    border: `1px solid ${token.border}`,
                    background: token.background,
                    padding: '8px 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {token.icon}

                    <Typography
                        style={{
                            color: token.color,
                            fontSize: 16,
                            fontWeight: 600,
                            fontFamily: 'Nunito',
                        }}
                    >
                        {label}
                    </Typography>
                </Box>

                {time && (
                    <Typography
                        style={{
                            color: token.color,
                            fontSize: 14,
                            fontWeight: 600,
                        }}
                    >
                        {time}
                    </Typography>
                )}
            </Box>

            {showConnector && (
                <Box
                    style={{
                        borderLeft: '2px dotted #000',
                        height: 20,
                        marginLeft: 28,
                    }}
                />
            )}
        </Box>
    );
};

export default function BackgroundVerification({ bgvStatusList }) {
    const list = Array.isArray(bgvStatusList) ? bgvStatusList : [];
    const events = list.map((item) => mapStatusToUI(item));
    const hasTimeline = events.length > 0;

    return (
        <Box
            style={{
                borderRadius: 16,
                border: '1px solid rgba(38, 50, 56, 0.16)',
                padding: 16,
                backgroundColor: '#fff',
                width: '100%',
                marginBottom: 80,
                marginTop: 30,
            }}
        >
            <Typography
                variant="h6"
                style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: '#14112d',
                    marginBottom: 12,
                }}
            >
                Background verification
            </Typography>

            {hasTimeline ? (
                <Box style={{ display: 'flex', flexDirection: 'column' }}>
                    {events.map((event, index) => (
                        <TimelineStep
                            key={index}
                            label={event.label}
                            time={event.time}
                            token={event.token}
                            showConnector={index !== events.length - 1}
                        />
                    ))}
                </Box>
            ) : (
                <Typography
                    style={{
                        fontSize: 14,
                        color: '#4c4e64',
                        fontWeight: 500,
                    }}
                >
                    Background verification details will appear here once the
                    check is initiated.
                </Typography>
            )}
        </Box>
    );
}
